/**
 * The synthesis engine.
 *
 * Shipping no audio files keeps the bundle small and sidesteps licensing, so
 * every sound is generated with the Web Audio API. The context is created
 * lazily on the first user gesture, which is what browsers require anyway.
 *
 * This file knows how to *make* a sound and nothing about what sounds mean —
 * the cues themselves, and which one a given impact should use, live in
 * soundboard.js.
 *
 * A cue is a list of layers, because a real impact is not one sound: it is a
 * transient, a body, and a tail. A layer is one source (noise or an
 * oscillator) through one filter into one envelope, which is enough to build
 * steel, stone, wood, cloth and glass out of the same few lines.
 *
 * Three things keep a dense battle from turning to mush:
 *
 *   - a limiter on the master bus, so peaks are held rather than clipped;
 *   - a voice budget, so a golem shedding masonry cannot start fifty voices;
 *   - clustering, so impacts landing within a few milliseconds of each other
 *     become one louder, pitch-spread sound instead of a machine-gun.
 */

import { CUES } from './soundboard.js';
import { pickSample } from './samples.js';

/** Simultaneous voices before new sounds start ducking, then dropping. */
const VOICE_BUDGET = 14;

/** Impacts closer together than this merge into one sound. */
const CLUSTER_WINDOW = 0.06;

/** Scheduling lookahead, so a cluster can still gather before it plays. */
const LOOKAHEAD = 0.025;

export class Sfx {
  constructor() {
    this.ctx = null;
    this.enabled = true;
    this.voices = 0;
    this.clusters = new Map();
    // Decoded once per URL, so only the very first play of a given file pays
    // for the fetch+decode round trip; every repeat after that is as cheap
    // as a synthesized cue.
    this.buffers = new Map();
  }

  /** Browsers require a user gesture before audio starts; call from a click. */
  resume() {
    if (!this.ctx) {
      const Ctx = window.AudioContext ?? window.webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();

      this.master = this.ctx.createGain();
      this.master.gain.value = this.enabled ? 0.7 : 0;

      // The limiter is what makes a rich mix safe: a dozen impacts in the same
      // half-second are held down together instead of clipping, so nothing has
      // to be thrown away to protect the output.
      this.limiter = this.ctx.createDynamicsCompressor();
      this.limiter.threshold.value = -8;
      this.limiter.knee.value = 4;
      this.limiter.ratio.value = 12;
      this.limiter.attack.value = 0.002;
      this.limiter.release.value = 0.18;

      this.master.connect(this.limiter).connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.master) this.master.gain.value = on ? 0.7 : 0;
  }

  /** Silences the board when the tab is hidden rather than playing to nobody. */
  setHidden(hidden) {
    if (!this.ctx) return;
    if (hidden) this.ctx.suspend();
    else if (this.enabled) this.ctx.resume();
  }

  /**
   * Plays a cue.
   *
   * `pitch` multiplies every frequency, `gain` every level, and `pan` places it
   * across the stereo field (-1..1) — impacts are panned by where they happen
   * on the board, which is the cheapest way to stop a busy mix sounding like
   * one point source.
   */
  play(name, { gain = 1, pitch = 1, pan = 0, spread = 0, when = 0 } = {}) {
    const cue = CUES[name];
    if (!cue || !this.enabled) return;
    this.resume();
    if (!this.ctx) return;

    // Beyond the budget, sounds duck and then stop starting at all — better a
    // thinner mix than a hundred voices fighting each other.
    if (this.voices >= VOICE_BUDGET) return;
    const budgetDuck = this.voices >= VOICE_BUDGET * 0.6
      ? Math.max(0.3, 1 - (this.voices - VOICE_BUDGET * 0.6) / (VOICE_BUDGET * 0.5))
      : 1;

    const at = this.ctx.currentTime + when;
    const level = gain * (cue.gain ?? 1) * budgetDuck;
    for (const layer of cue.layers) {
      // The budget is checked per layer, not per cue: a three-layer cue starts
      // three voices, so testing only on the way in lets the count overshoot.
      // Layers are ordered transient-first, so what gets dropped under load is
      // the tail — the part a crowded mix would bury anyway.
      if (this.voices >= VOICE_BUDGET) break;
      // A little jitter per layer so repeated hits are never bit-identical.
      const jitter = 1 + (Math.random() * 2 - 1) * (0.06 + spread);
      this.voice(layer, at + (layer.at ?? 0), level, pitch * jitter, pan);
    }
  }

  /**
   * Plays an impact, merging it with any other impact of the same cue that
   * lands within the cluster window.
   *
   * Eight chunks of masonry hitting the board in the same instant should be
   * one heavy crash, not eight thin ones — this is how a real mix handles
   * rubble, and it keeps every impact audible instead of discarding them.
   */
  impact(name, opts = {}) {
    if (!this.enabled) return;
    this.resume();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    const open = this.clusters.get(name);
    if (open && now - open.start < CLUSTER_WINDOW) {
      open.count += 1;
      // Each extra impact adds less than the last, and widens the pitch
      // spread so the cluster reads as several things rather than one loud one.
      open.gain = Math.min(open.gain + 0.55 / open.count, 1.6);
      open.spread = Math.min(open.spread + 0.05, 0.25);
      open.pan = (open.pan * (open.count - 1) + (opts.pan ?? 0)) / open.count;
      return;
    }

    const cluster = {
      start: now,
      count: 1,
      gain: opts.gain ?? 1,
      spread: opts.spread ?? 0,
      pan: opts.pan ?? 0,
      pitch: opts.pitch ?? 1
    };
    this.clusters.set(name, cluster);
    setTimeout(() => {
      this.clusters.delete(name);
      this.play(name, {
        gain: cluster.gain,
        pitch: cluster.pitch * (cluster.count > 1 ? 0.94 : 1),
        pan: cluster.pan,
        spread: cluster.spread
      });
    }, CLUSTER_WINDOW * 1000);
  }

  /**
   * A dropped-in voice sample for one of the four sample-eligible events
   * (select, confirm, attack, hurt), falling back to a synthesized cue when
   * the given event/pool has no files — see public/audio/sfx/README.md for
   * the drop-in convention this reads.
   *
   * Async, and deliberately not awaited by any call site: the first play of
   * a given file pays for a fetch+decode round trip and may land a beat late
   * against its animation cue, the same one-time cost `resume()` already
   * accepts for the audio context itself. Every repeat after that is instant
   * — the decoded buffer is cached — so this only ever costs once per file,
   * not once per play.
   */
  async voiceSample(event, pool, fallbackCue, opts = {}) {
    if (!this.enabled) return;
    const url = await pickSample(event, pool);
    if (!url) {
      this.play(fallbackCue, opts);
      return;
    }

    this.resume();
    if (!this.ctx) return;

    let buffer = this.buffers.get(url);
    if (!buffer) {
      try {
        const bytes = await (await fetch(url)).arrayBuffer();
        buffer = await this.ctx.decodeAudioData(bytes);
        this.buffers.set(url, buffer);
      } catch (err) {
        // A missing/corrupt file falls back exactly like an empty pool —
        // the drop-in feature must never be able to silence the game.
        console.warn(`[sfx] could not load sample "${url}", falling back`, err);
        this.play(fallbackCue, opts);
        return;
      }
    }
    if (!this.enabled || this.voices >= VOICE_BUDGET) return;

    const { gain = 1, pitch = 1, pan = 0, when = 0 } = opts;
    const at = this.ctx.currentTime + when;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = Math.max(0.3, pitch);

    let chain = source;
    const env = this.ctx.createGain();
    env.gain.value = Math.max(0, gain);
    chain = chain.connect(env);
    if (pan && this.ctx.createStereoPanner) {
      const panner = this.ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, pan));
      chain = chain.connect(panner);
    }
    chain.connect(this.master);

    this.voices += 1;
    source.onended = () => { this.voices = Math.max(0, this.voices - 1); };
    source.start(at);
  }

  /**
   * One layer: a source through a filter through an envelope.
   *
   * `src` is either 'noise' or an oscillator type. Noise is a decaying burst
   * baked into a buffer; oscillators get an optional exponential pitch ramp,
   * which is what turns a tone into a thud or a sweep.
   */
  voice(layer, when, level, pitch, pan) {
    const {
      src = 'noise', freq = 220, to, filter, cutoff = 1200, q = 1,
      attack = 0.004, decay = 0.12, gain = 0.2
    } = layer;
    const peak = Math.max(0.0002, gain * level);
    const ctx = this.ctx;

    let node;
    if (src === 'noise') {
      const length = Math.max(1, Math.floor(ctx.sampleRate * decay));
      const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / length);
      }
      node = ctx.createBufferSource();
      node.buffer = buffer;
    } else {
      node = ctx.createOscillator();
      node.type = src;
      const f = Math.max(20, freq * pitch);
      node.frequency.setValueAtTime(f, when);
      if (to !== undefined) {
        node.frequency.exponentialRampToValueAtTime(
          Math.max(20, to * pitch), when + decay
        );
      }
    }

    let chain = node;
    if (filter) {
      const biquad = ctx.createBiquadFilter();
      biquad.type = filter;
      // A resonant band is what separates metal from a dull knock; a plain
      // lowpass cannot ring however it is tuned.
      biquad.frequency.value = Math.max(20, cutoff * (src === 'noise' ? 1 : pitch));
      biquad.Q.value = q;
      chain = chain.connect(biquad);
    }

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, when);
    env.gain.exponentialRampToValueAtTime(peak, when + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, when + attack + decay);
    chain = chain.connect(env);

    if (pan && ctx.createStereoPanner) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, pan));
      chain = chain.connect(panner);
    }
    chain.connect(this.master);

    this.voices += 1;
    node.onended = () => { this.voices = Math.max(0, this.voices - 1); };
    node.start(when);
    node.stop(when + attack + decay + 0.05);
  }
}
